<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { OrderStatus } from '../../../enums/OrderEnums';
  import { IOrderEmailSettings } from '../../../interfaces/IOrderEmailSettings';
  import OrderEmailSettingsBasic from './OrderEmailSettingsBasic.vue';
  import OrderEmailSettingsAuth from './OrderEmailSettingsAuth.vue';
  import { vueData } from '../../../VueData';
  import BranchesService from '../../../api/services/branches.service';
  import { IBranch } from '../../../interfaces/IBranch';
  import { mergeObjects } from '../../../utils';
  import { showAsyncInputModal } from '../../../lib/modals';
  import { AlertType } from '../../../interfaces/IAlert';

  @Component({
    components: {
      OrderEmailAuth: OrderEmailSettingsAuth,
      OrderEmailBasic: OrderEmailSettingsBasic,
    },
  })
  export default class OrderEmailSettings extends Vue {
    @Prop() branch: IBranch;
    branchesService = new BranchesService();
    sendEmails: boolean = false;
    emailSettings: IOrderEmailSettings | null = null;
    tabIndex = 0;
    authValid = false;
    basicValid = false;
    saving = false;
    dirty = false;

    async created() {
      await this.loadSettings();
    }

    @Watch('branch')
    onBranchChanged(branch: IBranch) {
      this.loadSettings();
    }

    @Watch('emailSettings', { deep: true })
    onSettingsChanged() {
      this.dirty = true;
    }

    @Watch('sendEmails')
    onSendChanged() {
      this.dirty = true;
    }

    async loadSettings() {
      this.emailSettings = null;

      const defaultSettings: IOrderEmailSettings = {
        condition: OrderStatus.Received,
        when: 60,
        custom: true,
        html: null,
        basicHtml: true,
        subject: null,
        fromName: null,
        auth: {
          host: null,
          port: null,
          pw: null,
          user: null,
          secure: false,
        },
      };

      try {
        const settings = await this.branchesService.getMailSettings(
          this.branch.id
        );
        this.sendEmails = settings !== null;

        this.emailSettings = mergeObjects(defaultSettings, settings ?? {});

        this.$nextTick(() => (this.dirty = false));
      } catch (e) {
        vueData.serverError(e);
        console.error(e);
      }
    }

    async save() {
      try {
        this.saving = true;
        console.log(this.emailSettings);
        await this.branchesService.setMailSettings(this.branch.id, {
          settings: this.sendEmails ? this.emailSettings : null,
        });
        vueData.addAlert('Configuración guardada');
        this.dirty = false;
      } catch (e) {
        vueData.serverError(e);
        console.error(e);
      } finally {
        this.saving = false;
      }
    }

    async test() {
      await showAsyncInputModal(
        this.$modal,
        'Prueba de envío de email',
        'Ingrese dirección a la que enviar un mail de prueba. Recuerde revisar su casilla de spam.',
        async (input): boolean => {
          try {
            await this.branchesService.testMail(this.branch.id, input);
            vueData.addAlert('Mail enviado', AlertType.Info);
            return true;
          } catch (e) {
            vueData.serverError(e);
            console.error(e);
            return false;
          }
        }
      );
    }
  }
</script>

<template>
  <div>
    <v-skeleton-loader v-if="!emailSettings" type="paragraph" />
    <v-row v-else>
      <v-col cols="12">
        <v-checkbox
          label="Enviar correo electrónico a clientes tras realizar un pedido"
          v-model="sendEmails"
          hide-details
          class="mt-0 mb-2"
        />
        <template v-if="sendEmails">
          <p class="mb-0 grey--text">
            Al hacer un pedido, a los clientes se les permitira ingresar
            opcionalmente una dirección de email. De hacerlo, recibirán un email
            según la siguiente configuración.
          </p>
          <hr class="my-4" />
          <v-tabs v-model="tabIndex">
            <v-tab
              >Básico
              <v-chip v-if="!basicValid" color="red" dark x-small class="ml-2"
                >!</v-chip
              ></v-tab
            >
            <v-tab
              >Autenticación
              <v-chip v-if="!authValid" color="red" dark x-small class="ml-2"
                >!</v-chip
              >
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="tabIndex">
            <v-tab-item class="pt-4" eager
              ><OrderEmailBasic :settings="emailSettings" v-model="basicValid"
            /></v-tab-item>
            <v-tab-item class="pt-4" eager
              ><OrderEmailAuth :settings="emailSettings" v-model="authValid"
            /></v-tab-item>
          </v-tabs-items>
        </template>
      </v-col>
    </v-row>
    <div class="actions text-center">
      <v-btn
        v-if="sendEmails"
        color="info"
        class="mr-2"
        :disabled="dirty || !basicValid || !authValid"
        @click="test"
        >Probar configuración</v-btn
      >
      <v-btn
        color="primary"
        :disabled="!authValid || !basicValid || !dirty"
        @click="save"
        :loading="saving"
        >{{ $t('save') }}</v-btn
      >
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
